"use client";

import {
  type ChangeEvent,
  type ChangeEventHandler,
  type FocusEventHandler,
  type InputHTMLAttributes,
  type MouseEventHandler,
  type Ref,
  useMemo,
  useRef,
  useState,
  type FC,
} from "react";
import { isEmpty, mergeClasses } from "@huub-web/utils";

import { mergeRefs } from "../../utils";
import type { ValidatableProps } from "../../props";
import type { FieldMetadata } from "../../contexts/FormContext";
import { useFormValidation } from "../../hooks/useFormValidation";
import { BaseInputField, type BaseInputFieldProps } from "../BaseInputField";
import type { TypographyVariant } from "../../theme/types/TypographyVariant";

import {
  inputStyle,
  placeholderStyle,
} from "../BaseInputField/BaseInputField.css";
import { typography } from "../../theme/css/typography.css";

export type TextFieldProps = Omit<BaseInputFieldProps, "placeholder"> &
  Pick<
    InputHTMLAttributes<HTMLInputElement>,
    | "placeholder"
    | "type"
    | "maxLength"
    | "inputMode"
    | "onKeyDown"
    | "onKeyUp"
    | "onFocus"
    | "onBlur"
    | "max"
    | "min"
    | "autoComplete"
    | "spellCheck"
    | "autoCapitalize"
  > &
  ValidatableProps & {
    onChange?: TextFieldChangeHandler;
    inputRef?: Ref<HTMLInputElement>;
    value?: string;
    defaultValue?: string;
    typography?: TypographyVariant;
    ignorePasswordManagers?: boolean;
    /**
     * The backend field key for this input (e.g. "applicant.income").
     * Passed by FormRow via cloneElement from the data-field-key attribute.
     * Forwarded to useFormValidation for AI agent field awareness.
     */
    fieldKey?: string;
    /**
     * Override the field type used for AI agent metadata registration.
     * When set, this is used instead of the HTML input `type` prop.
     * Useful for NumberField which uses type="text" for formatting
     * but should register as type "number" for the agent.
     */
    fieldType?: string;
    /**
     * Rich input type (e.g. "money", "percentage") from FormInputType.
     * Included in field metadata so the AI agent can format values
     * in summaries (e.g. prepend €, append %).
     */
    inputType?: string;
  };

export type TextFieldChangeHandler = (
  value: string,
  e: ChangeEvent<HTMLInputElement>,
) => void;

/**
 * TextField is a component that wraps around the base input field with a text input.
 */
export const TextField: FC<TextFieldProps> = ({
  id: idProp,
  error: errorProp,
  disabled: disabledProp,
  placeholder,
  type = "text",
  value,
  defaultValue,
  variant = "default",
  width = "stretch",
  autoFocus,
  inputMode,
  inputRef,
  align,
  maxLength,
  min,
  max,
  autoComplete = "off",
  spellCheck = false,
  validation,
  autoCapitalize,
  typography: typographyProp,
  name,
  label,
  fieldKey,
  fieldType,
  inputType,
  ignorePasswordManagers = true,
  className,
  onChange,
  onClick,
  onKeyDown,
  onKeyUp,
  onFocus,
  onBlur,
  ref,
  ...props
}) => {
  const internalInputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);

  const handleClick: MouseEventHandler<HTMLInputElement> = (e) => {
    internalInputRef.current?.focus();
    onClick?.(e);
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    onChange?.(e.target.value, e);
  };

  const handleFocus: FocusEventHandler<HTMLInputElement> = (e) => {
    setFocused(true);
    onFocus?.(e);
  };

  const handleBlur: FocusEventHandler<HTMLInputElement> = (e) => {
    setFocused(false);
    onBlur?.(e);
  };

  const metadata = useMemo<FieldMetadata>(
    () => ({
      label: label as string | undefined,
      type:
        (fieldType as FieldMetadata["type"]) ??
        (type === "number" ? "number" : type === "date" ? "date" : "text"),
      ...(inputType ? { inputType } : {}),
      required: validation?.includes("required"),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [label, fieldType, inputType, type, validation],
  );

  const { id, error, disabled } = useFormValidation(value, {
    id: idProp,
    error: errorProp,
    disabled: disabledProp,
    validation,
    name,
    ref: internalInputRef,
    fieldKey,
    metadata,
  });

  if (type === "hidden") {
    return (
      <input
        ref={mergeRefs(internalInputRef, inputRef)}
        id={id}
        name={name}
        type="hidden"
        value={value ?? ""}
      />
    );
  }

  return (
    <BaseInputField
      ref={ref}
      cursor="text"
      onClick={handleClick}
      disabled={disabled}
      variant={variant}
      align={align}
      width={width}
      error={error}
      label={label}
      labelFloat={isEmpty(value) && !focused && !placeholder}
      className={mergeClasses(
        typographyProp
          ? typography[typographyProp]
          : typography[variant === "toggle" ? "bodyL" : "bodyM"],
        className,
      )}
      {...props}
    >
      <input
        ref={mergeRefs(internalInputRef, inputRef)}
        id={id}
        className={mergeClasses(
          placeholderStyle,
          inputStyle({ disabled, align }),
        )}
        disabled={disabled}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        type={type}
        value={value ?? ""}
        defaultValue={defaultValue}
        inputMode={inputMode}
        maxLength={maxLength}
        min={min}
        max={max}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        autoComplete={autoComplete}
        spellCheck={spellCheck}
        autoCapitalize={autoCapitalize}
        {...(ignorePasswordManagers ? { "data-1p-ignore": true } : {})}
      />
    </BaseInputField>
  );
};
